import React, { useCallback, useEffect, useMemo, useRef } from 'react'

import { AIChatResponseEventFragment } from 'types/graphql'

import AIChatInputArea from 'src/components/AIChatInputArea'
import {
  CitizenAIChatMessage,
  CitizenAIViewMode,
  useCitizenAIAssistant,
} from 'src/components/CitizenAIAssistant/CitizenAIAssistantContext'
import ContentAreaOverlay from 'src/components/ContentAreaOverlay/ContentAreaOverlay'
import { useCreateAIChatMutation, useSubscribeToAIChatSessionResponses } from 'src/fetch/aiChat'
import { useGetOrganizationForAIChatWindowQuery } from 'src/fetch/organizations'
import { Icon, T, VStack } from 'src/govwell-ui'
import { useIsInProductAICAEnabled } from 'src/utils/featureFlagging/featureFlagging'

import CitizenAIChatHeader from './CitizenAIChatHeader'
import CitizenAIChatMessageItem from './CitizenAIChatMessageItem'
import CitizenTownSelector from './CitizenTownSelector'

const updateLastMessage = (
  update: (message: CitizenAIChatMessage) => CitizenAIChatMessage
): React.SetStateAction<CitizenAIChatMessage[]> => {
  return (prev) => {
    const lastMessage = prev[prev.length - 1]
    if (!lastMessage) {
      return prev
    }
    return [...prev.slice(0, -1), update(lastMessage)]
  }
}

const TOWN_SUGGESTION_MARKER = 'data-town-suggestion'

const ORG_SWITCH_FALLBACK_PHRASES = [
  'different municipality',
  'different jurisdiction',
  'different organization',
  'different town',
  'different county',
  'contact their staff',
  'visit their official website',
]

const responseIndicatesOrgSwitch = (text: string): boolean => {
  const lower = text.toLowerCase()
  return (
    lower.includes(TOWN_SUGGESTION_MARKER) ||
    ORG_SWITCH_FALLBACK_PHRASES.some((phrase) => lower.includes(phrase))
  )
}

const DEFAULT_PROMPTS = [
  'Help me find the right form',
  'What things can you help me with?',
  'Look up my permits',
]

const GENERIC_MODE_PROMPTS = [
  'What permits do I need for a home renovation?',
  'How do I check the status of my application?',
  'What can you help me with?',
]

type CitizenAIAssistantOverlayContentProps = {
  onClose: () => void
  viewMode: CitizenAIViewMode
  onMaximize: () => void
  onMinimize: () => void
}

export const CitizenAIAssistantOverlayContent = ({
  onClose,
  viewMode,
  onMaximize,
  onMinimize,
}: CitizenAIAssistantOverlayContentProps) => {
  const context = useCitizenAIAssistant()
  const userTextInput = context?.userTextInput ?? ''
  const setUserTextInput = context?.setUserTextInput ?? (() => {})
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const messages = context?.messages ?? []
  const setMessages = context?.setMessages ?? (() => {})
  const isAssistantTyping = context?.isAssistantTyping ?? false
  const setIsAssistantTyping = context?.setIsAssistantTyping ?? (() => {})
  const isGenericMode = context?.isGenericMode ?? true
  const currentOrgContext = context?.currentOrgContext
  // TODO: memoize this fallback — will be addressed in the next PR when we handle context switching more gracefully
  const setCurrentOrgContext = context?.setCurrentOrgContext ?? (() => {})

  const chatLocalStorageId = context?.chatStorageIds.chatLocalStorageId ?? ''
  const chatSessionStorageId = context?.chatStorageIds.chatSessionStorageId ?? ''

  const { mutateAsync: createAIChat } = useCreateAIChatMutation()

  const { organization } = useGetOrganizationForAIChatWindowQuery(currentOrgContext?.orgSlug ?? '')

  useEffect(() => {
    const displayName = organization?.displayName ?? organization?.city
    if (displayName && currentOrgContext?.orgSlug && !currentOrgContext.orgDisplayName) {
      setCurrentOrgContext({
        orgSlug: currentOrgContext.orgSlug,
        orgDisplayName: displayName,
      })
    }
  }, [organization, currentOrgContext, setCurrentOrgContext])

  const onResponseEvent = useCallback(
    (event: AIChatResponseEventFragment) => {
      if (event.__typename === 'AIChatTextUpdate') {
        setMessages(
          updateLastMessage((lastMessage) => ({
            ...lastMessage,
            text: event.fullText,
            isThinking: !event.done,
          }))
        )
      } else if (event.__typename === 'AIChatTextDelta') {
        setMessages(
          updateLastMessage((lastMessage) => ({
            ...lastMessage,
            text: lastMessage.text + (event.text || ''),
            isThinking: !event.done,
          }))
        )
      }
    },
    [setMessages]
  )

  useSubscribeToAIChatSessionResponses({
    chatSessionStorageId,
    onResponseEvent,
  })

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSendMessage = useCallback(
    async (autoSendText?: string) => {
      const textMessage = autoSendText ?? userTextInput
      if (!textMessage.trim() || isAssistantTyping) {
        return
      }

      setMessages((prev) => [
        ...prev,
        {
          id: `user-${Date.now()}`,
          text: textMessage,
          sender: 'user',
          questionCreatedAt: new Date().toISOString(),
        },
        {
          id: `ai-${Date.now()}`,
          text: '',
          sender: 'ai',
          isThinking: true,
        },
      ])
      setUserTextInput('')
      setIsAssistantTyping(true)

      try {
        const result = await createAIChat({
          input: {
            chatLocalStorageId,
            chatSessionStorageId,
            orgSlug: currentOrgContext?.orgSlug || undefined,
            question: textMessage,
            source: 'GovWell',
            allowTownSwitching: true,
          },
        })

        setMessages(
          updateLastMessage((lastMessage) => ({
            ...lastMessage,
            id: result.createAIChat.id,
            questionCreatedAt: result.createAIChat.questionCreatedAt ?? '',
            responseCreatedAt: result.createAIChat.responseCreatedAt ?? '',
            isThinking: false,
            ...(result.createAIChat.response ? { text: result.createAIChat.response } : {}),
            ...(result.createAIChat.citations?.length
              ? { citations: result.createAIChat.citations }
              : {}),
          }))
        )
      } catch {
        setMessages(
          updateLastMessage((lastMessage) => ({
            ...lastMessage,
            text: '<div>Sorry, something went wrong. Please try again.</div>',
            isThinking: false,
          }))
        )
      } finally {
        setIsAssistantTyping(false)
      }
    },
    [
      userTextInput,
      isAssistantTyping,
      setMessages,
      setUserTextInput,
      setIsAssistantTyping,
      createAIChat,
      chatLocalStorageId,
      chatSessionStorageId,
      currentOrgContext?.orgSlug,
    ]
  )

  const handleSelectPrompt = useCallback(
    (prompt: string) => {
      void handleSendMessage(prompt)
    },
    [handleSendMessage]
  )

  const displayPrompts = useMemo(
    () => (isGenericMode ? GENERIC_MODE_PROMPTS : DEFAULT_PROMPTS),
    [isGenericMode]
  )

  const hasMessages = messages.length > 0

  const lastAIMessage = useMemo(
    () => [...messages].reverse().find((msg) => msg.sender === 'ai'),
    [messages]
  )
  const showOrgSelectorAfterMessages =
    !isAssistantTyping &&
    !!lastAIMessage &&
    !lastAIMessage.isThinking &&
    responseIndicatesOrgSwitch(lastAIMessage.text)

  const SideViewEmptyState = useMemo(
    () => (
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-8">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-neutral-900">
          <Icon.FaSparkles className="h-7 w-7 text-white" />
        </div>
        <T.Body className="mb-1 text-center font-semibold text-neutral-900">
          {isGenericMode || !currentOrgContext?.orgDisplayName
            ? 'GovWell AI Assistant'
            : `${currentOrgContext.orgDisplayName} Assistant`}
        </T.Body>
        <T.BodySmall className="mb-4 text-center text-neutral-600">
          {isGenericMode
            ? 'Ask me anything about GovWell, permits, or government processes.'
            : 'Ask about permits, forms, fees, and requirements.'}
        </T.BodySmall>
      </div>
    ),
    [isGenericMode, currentOrgContext?.orgDisplayName]
  )

  return (
    <div className="flex h-full bg-neutral-50">
      <div className="flex min-w-0 flex-1 flex-col">
        <CitizenAIChatHeader
          onClose={onClose}
          viewMode={viewMode}
          onMaximize={onMaximize}
          onMinimize={onMinimize}
          showSidebar={false}
        />

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {!hasMessages ? (
            SideViewEmptyState
          ) : (
            <div className="mx-auto w-full flex-1 px-4 py-4">
              <VStack gap={4} align="stretch">
                {messages.map((msg) => (
                  <CitizenAIChatMessageItem key={msg.id} message={msg} />
                ))}
                {showOrgSelectorAfterMessages && <CitizenTownSelector />}
                <div ref={messagesEndRef} />
              </VStack>
            </div>
          )}
        </div>

        <AIChatInputArea
          value={userTextInput}
          onValueChange={setUserTextInput}
          onSend={() => handleSendMessage()}
          isDisabled={isAssistantTyping}
          suggestedPrompts={displayPrompts}
          onSelectPrompt={handleSelectPrompt}
          showPrompts={!hasMessages}
        />
      </div>
    </div>
  )
}

const CitizenAIAssistantOverlay = () => {
  const isInProductAICAEnabled = useIsInProductAICAEnabled()
  const context = useCitizenAIAssistant()

  if (!isInProductAICAEnabled || !context) {
    return null
  }

  const { viewMode, overlay, deferredClose, maximize, minimize } = context

  if (viewMode === 'side') {
    return null
  }

  return (
    <ContentAreaOverlay isOpen={overlay.isOpen} onClose={deferredClose}>
      <CitizenAIAssistantOverlayContent
        onClose={overlay.close}
        viewMode={viewMode}
        onMaximize={maximize}
        onMinimize={minimize}
      />
    </ContentAreaOverlay>
  )
}

export default React.memo(CitizenAIAssistantOverlay)
